import { servicesUrlConfig } from "shared/config/setting";

/**
 * Organizations API Service
 * Centralized API calls for organization management
 */
export const organizationsService = {
  /**
   * Get all organizations with pagination
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 10)
   * @returns {Promise} API response with organizations data
   */
  async getAll(authFetch, page = 1, perPage = 10) {
    const url = `${servicesUrlConfig.ORGS_API.get_all}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get organizations and roles for current user
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 10)
   * @returns {Promise} API response with user's organizations and roles
   */
  async getMyOrganizationsRoles(authFetch, page = 1, perPage = 10) {
    const url = `${servicesUrlConfig.ORGS_API.me}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get organization details by ID
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with organization details
   */
  async getById(orgId, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.get_by_id}${orgId}`;
    return await authFetch(url);
  },

  /**
   * Create a new organization
   * @param {Object} orgData - Organization data {name, description}
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async create(orgData, authFetch) {
    const url = servicesUrlConfig.ORGS_API.create;
    return await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgData),
    });
  },

  /**
   * Update an existing organization
   * @param {string} orgId - Organization ID
   * @param {Object} updateData - Data to update {name, description}
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async update(orgId, updateData, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.update}${orgId}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Delete an organization
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async delete(orgId, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.delete}${orgId}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },

  /**
   * Add a member to an organization
   * @param {string} orgId - Organization ID
   * @param {string} username - Username to add
   * @param {Array} roles - Array of roles to assign
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async addMember(orgId, username, roles, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.add_member}${orgId}/members`;
    return await authFetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, roles }),
    });
  },

  /**
   * Update member roles in an organization
   * @param {string} orgId - Organization ID
   * @param {string} username - Username to update
   * @param {Array} roles - New array of roles
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async updateMemberRoles(orgId, username, roles, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.remove_member}${orgId}/members/${username}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      // Backend expects the roles as a bare JSON array, not an object
      body: JSON.stringify(roles),
    });
  },

  /**
   * Remove a member from an organization
   * @param {string} orgId - Organization ID
   * @param {string} username - Username to remove
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async removeMember(orgId, username, authFetch) {
    const url = `${servicesUrlConfig.ORGS_API.remove_member}${orgId}/members/${username}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },

  /**
   * Get members of an organization
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 10; 0 fetches all)
   * @returns {Promise} API response with paginated members data
   */
  async getOrgMembers(orgId, authFetch, page = 1, perPage = 10) {
    const url = `${servicesUrlConfig.ORGS_API.get_by_id}${orgId}/members?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get all robots in an organization
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 0 to fetch all)
   * @returns {Promise} API response with paginated robots data
   */
  async getOrgRobots(orgId, authFetch, page = 1, perPage = 0) {
    const url = `${servicesUrlConfig.ORGS_API.get_by_id}${orgId}/robots?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Add a robot to an organization
   * @param {string} orgId - Organization ID
   * @param {string} robotId - Robot ID to add
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async addRobot(orgId, robotId, authFetch) {
    // Backend takes robot_id as a query parameter, no request body
    const url = `${servicesUrlConfig.ORGS_API.add_robot}${orgId}/robots?robot_id=${encodeURIComponent(robotId)}`;
    return await authFetch(url, {
      method: "POST",
    });
  },

  /**
   * Remove a robot from an organization
   * @param {string} orgId - Organization ID
   * @param {string} robotId - Robot ID to remove
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async removeRobot(orgId, robotId, authFetch) {
    // Backend takes robot_id as a query parameter, no request body
    const url = `${servicesUrlConfig.ORGS_API.remove_robot}${orgId}/robots?robot_id=${encodeURIComponent(robotId)}`;
    return await authFetch(url, {
      method: "DELETE",
    });
  },
};
